import { useState, type FormEvent } from 'react'
import type { WorkerProfile } from '../../roster/index.js'
import type { WorkerRole, WorkerTier } from '../../core/index.js'
import { ADMIN_API_BASE_URL, EndpointHint } from '../../core/index.js'
import { sendWorkerOnboard, type PartnerSession } from '../lib/partnerApi.js'
import { TIER_LABELS } from '../../core/index.js'
import { WORKER_ROLE_LABELS } from '../../core/index.js'
import { Send } from 'lucide-react'
import { ErrorNote, INPUT_CLASS, Labeled, SuccessNote, describeError } from './formControls.js'
import { Button } from '../../core/index.js'

const ROLES = Object.keys(WORKER_ROLE_LABELS) as WorkerRole[]
const TIERS = Object.keys(TIER_LABELS) as WorkerTier[]
// Mirrors the API's workerId rule, so a typo is caught before the request.
const WORKER_ID_PATTERN = /^[a-zA-Z0-9-]+$/

interface OnboardFields {
  workerId: string
  name: string
  role: WorkerRole
  hireDate: string
  /** Empty means "let the API decide" - it defaults a new hire to probationary. */
  initialTier: WorkerTier | ''
}

const EMPTY_FIELDS: OnboardFields = { workerId: '', name: '', role: 'CNA', hireDate: new Date().toISOString().slice(0, 10), initialTier: '' }

interface WorkerOnboardFormProps {
  session: PartnerSession
  facilityId: string
  /** Called after a worker is created, so the roster shown beside the form can refresh. */
  onOnboarded: () => void
}

/** Sends `POST /events/worker-onboard` as the partner: a new hire joining this facility's roster. */
export function WorkerOnboardForm({ session, facilityId, onOnboarded }: WorkerOnboardFormProps) {
  const [fields, setFields] = useState<OnboardFields>(EMPTY_FIELDS)
  const [isSending, setIsSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [created, setCreated] = useState<WorkerProfile | null>(null)

  const workerId = fields.workerId.trim()
  const isWorkerIdValid = WORKER_ID_PATTERN.test(workerId)
  const canSend = isWorkerIdValid && fields.name.trim() !== '' && fields.hireDate !== '' && !isSending

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault()
    if (!canSend || !ADMIN_API_BASE_URL) {
      return
    }
    setIsSending(true)
    setError(null)
    setCreated(null)
    try {
      const profile = await sendWorkerOnboard(ADMIN_API_BASE_URL, session, {
        facility_id: facilityId,
        worker_id: workerId,
        name: fields.name.trim(),
        role: fields.role,
        hire_date: fields.hireDate,
        ...(fields.initialTier === '' ? {} : { initial_tier: fields.initialTier }),
      })
      setCreated(profile)
      setFields(EMPTY_FIELDS)
      onOnboarded()
    } catch (err) {
      setError(describeError(err, 'The event could not be sent.'))
    } finally {
      setIsSending(false)
    }
  }

  return (
    <form onSubmit={(event) => void handleSubmit(event)} className="space-y-4">
      <div className="grid gap-5 sm:grid-cols-2">
        <Labeled
          label="Worker ID"
          htmlFor="onboard-worker-id"
          hint={workerId !== '' && !isWorkerIdValid ? 'Letters, numbers and hyphens only.' : "The partner's identifier for this person."}
        >
          <input
            id="onboard-worker-id"
            className={`${INPUT_CLASS} font-mono`}
            value={fields.workerId}
            onChange={(event) => setFields({ ...fields, workerId: event.target.value })}
          />
        </Labeled>
        <Labeled label="Name" htmlFor="onboard-name">
          <input id="onboard-name" className={INPUT_CLASS} value={fields.name} onChange={(event) => setFields({ ...fields, name: event.target.value })} />
        </Labeled>
        <Labeled label="Role" htmlFor="onboard-role">
          <select id="onboard-role" className={INPUT_CLASS} value={fields.role} onChange={(event) => setFields({ ...fields, role: event.target.value as WorkerRole })}>
            {ROLES.map((role) => (
              <option key={role} value={role}>
                {role} - {WORKER_ROLE_LABELS[role]}
              </option>
            ))}
          </select>
        </Labeled>
        <Labeled label="Hire date" htmlFor="onboard-hire-date">
          <input
            id="onboard-hire-date"
            type="date"
            className={INPUT_CLASS}
            value={fields.hireDate}
            onChange={(event) => setFields({ ...fields, hireDate: event.target.value })}
          />
        </Labeled>
        <Labeled label="Initial tier" htmlFor="onboard-tier" hint="Optional. Left unset, a new hire starts as probationary.">
          <select
            id="onboard-tier"
            className={INPUT_CLASS}
            value={fields.initialTier}
            onChange={(event) => setFields({ ...fields, initialTier: event.target.value as WorkerTier | '' })}
          >
            <option value="">Not set</option>
            {TIERS.map((tier) => (
              <option key={tier} value={tier}>
                {TIER_LABELS[tier]}
              </option>
            ))}
          </select>
        </Labeled>
      </div>

      <Button icon={Send} size="lg" type="submit" disabled={!canSend}>
        {isSending ? 'Sending…' : 'Send worker-onboard'}
      </Button>
      <EndpointHint className="ml-3">POST /events/worker-onboard</EndpointHint>

      {error && <ErrorNote message={error} />}
      {created && (
        <SuccessNote>
          <span className="font-bold">{created.name}</span> (<span className="font-mono">{created.workerId}</span>) joined the roster as {created.role},{' '}
          {TIER_LABELS[created.tier]} tier, score {created.score}.
        </SuccessNote>
      )}
    </form>
  )
}
